import React, { useCallback, useEffect, useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";

const EDGE_THRESHOLD = 24; // px tolerance for "already at top/bottom"

// Floating scroll-to-top / scroll-to-bottom pair for a tall scrollable
// panel. Pass the ref of the actual `overflow-y-auto` element via
// `scrollRef` — this renders as an *absolutely positioned sibling* that
// must sit outside that scrolling element (inside a shared `relative`
// ancestor), so it stays put in the corner instead of scrolling away with
// the content. Each arrow hides itself once you're already at that edge.
const ScrollToTopBottom = ({ scrollRef }) => {
  const [atTop, setAtTop] = useState(true);
  const [atBottom, setAtBottom] = useState(true);

  const updateEdges = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setAtTop(el.scrollTop <= EDGE_THRESHOLD);
    setAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - EDGE_THRESHOLD);
  }, [scrollRef]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateEdges();

    el.addEventListener("scroll", updateEdges, { passive: true });
    window.addEventListener("resize", updateEdges);
    // Comments expanding/collapsing, new comments/likes, etc. all change
    // the scrollable content's height without necessarily firing a scroll
    // event — re-check on any DOM change inside the panel too.
    const mutationObserver = new MutationObserver(updateEdges);
    mutationObserver.observe(el, { childList: true, subtree: true });

    return () => {
      el.removeEventListener("scroll", updateEdges);
      window.removeEventListener("resize", updateEdges);
      mutationObserver.disconnect();
    };
  }, [scrollRef, updateEdges]);

  if (atTop && atBottom) return null;

  const scrollToTop = () => scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  const scrollToBottom = () =>
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });

  return (
    <div className="absolute bottom-5 right-5 flex flex-col gap-2 z-10">
      {!atTop && (
        <button
          type="button"
          onClick={scrollToTop}
          title="Scroll to top"
          className="w-9 h-9 rounded-full bg-surface/95 backdrop-blur-md border border-border shadow-md flex items-center justify-center text-text_secondary"
        >
          <ChevronUp size={16} />
        </button>
      )}
      {!atBottom && (
        <button
          type="button"
          onClick={scrollToBottom}
          title="Scroll to bottom"
          className="w-9 h-9 rounded-full bg-surface/95 backdrop-blur-md border border-border shadow-md flex items-center justify-center text-text_secondary"
        >
          <ChevronDown size={16} />
        </button>
      )}
    </div>
  );
};

export default ScrollToTopBottom;
